// Alert bell + dropdown panel.
// Ported from V1 public/app/index.html — generateAlerts (L6562), updateAlertBell
// (L7079), toggleAlertPanel (L7089), _getDismissedAlerts (L9633), dismissAlert
// (L9650), clearAllDismissableAlerts (L9657).
//
// Mirrors V1 dismiss persistence via localStorage key `jsp_dismissed_alerts`
// (same key V1 uses) so alerts dismissed in one build stay dismissed in the
// other.
//
// Alert sources wired here:
//  - overdue payments          (opts.alertOverdue)
//  - unpaid expenses           (opts.alertUnpaidExpense + alertUnpaidDays)
//  - over-budget projects      (opts.alertOverBudget)
//  - near-budget projects      (opts.alertBudgetWarn)
//  - reminders today / overdue (opts.alertReminders)
//  - stale projects            (opts.alertStaleProject + alertStaleDays)
//  - estimate expired/expiring
//  - estimate approved/rejected
//  - client portal accepted/declined
//  - 1099 threshold            (opts.flag1099 / opts.warn1099)
//  - recurring expense due
//  - biz doc (insurance/bond/WC) expiring or expired
//  - incoming shared estimates (useIncomingSharedEstimates — estshare__ docs)
//
// Not wired (no V2 event stream yet):
//  - portal view events (opts.portalViewEvents) — V1 tracks these via portal
//    pings; V2 portal plumbing lands later.
//  - generic submissionEvents — same.
//  - alertNewBid — V1 logs a submissionEvent when a bid lands; V2 has bids in
//    appstate but no event stream, so we do not double-count.
//  - pending contractor submissions — already surfaced by the Shell banner via
//    useAssignmentsRealtime; adding them here would double-count.
// The chrome is in place, so adding each source later is a one-block insert.
//
// Deep-link navigation uses react-router. V1's openProject / openStandaloneEstimate
// are not ported as separate flows — we navigate to the list view instead.
//
// Outside-click close: window pointerdown listener, ref on the wrap.
// Escape key closes panel.

import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useAppState } from '../hooks/useAppState'
import { useOptsMutation } from '../hooks/useOptsMutation'
import { useIncomingSharedEstimates } from '../hooks/useIncomingSharedEstimates'
import { fmt, fmtDate, pct, today as isoToday } from '../lib/fmt'
import type { AppState } from '../types/firestore'

interface AlertItem {
  type: string
  icon: string
  iconBg: string
  title: string
  desc: string
  action: () => void
  priority: number
  dismissable?: boolean
  key?: string
  /** Portal tokens to clear from opts.portalUnreadTokens when this alert is
   *  actioned or dismissed. Populated by portal_accepted/portal_declined rows. */
  portalTokens?: string[] | undefined
}

const DISMISSED_KEY = 'jsp_dismissed_alerts'

function getDismissed(): Record<string, number> {
  try {
    const raw = localStorage.getItem(DISMISSED_KEY)
    return raw ? (JSON.parse(raw) as Record<string, number>) : {}
  } catch { return {} }
}

function setDismissed(d: Record<string, number>): void {
  try { localStorage.setItem(DISMISSED_KEY, JSON.stringify(d)) } catch { /* noop */ }
}

function daysBetween(a: Date, b: Date): number {
  return Math.round((a.getTime() - b.getTime()) / 86_400_000)
}

interface GenerateContext {
  state: AppState
  navigate: (path: string) => void
  dismissed: Record<string, number>
}

/**
 * Build the alert list from appstate. Returns items in priority order,
 * filtered by already-dismissed keys. Dismissable items carry a stable `key`
 * that V1 writes to the same localStorage slot.
 */
function generateAlerts({ state, navigate, dismissed }: GenerateContext): AlertItem[] {
  const out: AlertItem[] = []
  const today = isoToday()
  const now = new Date()
  const {
    projects, expenses, payments, reminders, schedules, estimates, subs, opts,
  } = state

  // ── 1099 thresholds ─────────────────────────────────────────────────────
  if (opts.flag1099 || opts.warn1099) {
    const year = now.getFullYear()
    const byWho: Record<number, number> = {}
    for (const e of expenses) {
      if (e.whoId == null) continue
      if (new Date(e.date).getFullYear() !== year) continue
      byWho[e.whoId] = (byWho[e.whoId] ?? 0) + e.amount
    }
    for (const s of subs) {
      const total = byWho[s.id] ?? 0
      if (opts.flag1099 && total >= 600) {
        out.push({
          type: '1099', icon: '📋', iconBg: 'var(--red-bg)',
          title: `1099 required — ${s.name}`,
          desc: `${fmt(total)} paid in ${year}. File a 1099-NEC.`,
          action: () => navigate('/subs'), priority: 2,
        })
      } else if (opts.warn1099 && total >= 500 && total < 600) {
        out.push({
          type: '1099_warn', icon: '⚠️', iconBg: 'var(--amber-bg)',
          title: `Approaching 1099 — ${s.name}`,
          desc: `${fmt(total)} paid in ${year}. $${600 - Math.round(total)} until threshold.`,
          action: () => navigate('/subs'), priority: 3,
        })
      }
    }
  }

  // ── Overdue payments ────────────────────────────────────────────────────
  if (opts.alertOverdue) {
    for (const p of payments) {
      if (p.status !== 'overdue') continue
      const proj = projects.find((x) => x.id === p.projId)
      out.push({
        type: 'overdue', icon: '🔴', iconBg: 'var(--red-bg)',
        title: `Overdue payment — ${fmt(p.amount)}`,
        desc: `${proj ? proj.name : 'Unknown project'} · ${p.type ?? 'Payment'} · Due ${p.date}`,
        action: () => navigate('/payments'), priority: 1,
      })
    }
  }

  // ── Unpaid expenses (older than alertUnpaidDays) ────────────────────────
  if (opts.alertUnpaidExpense) {
    const threshold = new Date(now)
    threshold.setDate(threshold.getDate() - (opts.alertUnpaidDays ?? 14))
    for (const e of expenses) {
      if (e.paid !== 'no' && e.paid !== false) continue
      if (new Date(e.date) >= threshold) continue
      const proj = projects.find((x) => x.id === e.projId)
      const age = daysBetween(now, new Date(e.date))
      out.push({
        type: 'unpaid', icon: '💸', iconBg: 'var(--amber-bg)',
        title: `Unpaid expense — ${fmt(e.amount)}`,
        desc: `${e.whoName ?? ''}${proj ? ` · ${proj.name}` : ''} · ${age} days old`,
        action: () => navigate('/expenses'), priority: 2,
      })
    }
  }

  // ── Over-budget / near-budget ───────────────────────────────────────────
  if (opts.alertOverBudget || opts.alertBudgetWarn) {
    for (const p of projects) {
      if (p.status !== 'In progress' || !p.budget || p.budget <= 0) continue
      const spent = expenses
        .filter((e) => e.projId === p.id)
        .reduce((sum, e) => sum + (e.amount || 0), 0)
      if (opts.alertOverBudget && spent > p.budget) {
        out.push({
          type: 'over_budget', icon: '📊', iconBg: 'var(--red-bg)',
          title: `Over budget — ${p.name}`,
          desc: `${fmt(spent)} spent on ${fmt(p.budget)} budget (+${fmt(spent - p.budget)})`,
          action: () => navigate('/projects'), priority: 1,
        })
      } else if (opts.alertBudgetWarn && spent > 0.9 * p.budget && spent <= p.budget) {
        out.push({
          type: 'budget_warn', icon: '📊', iconBg: 'var(--amber-bg)',
          title: `Near budget limit — ${p.name}`,
          desc: `${pct(spent, p.budget)}% used · ${fmt(p.budget - spent)} remaining`,
          action: () => navigate('/projects'), priority: 3,
        })
      }
    }
  }

  // ── Reminders (today + overdue) ─────────────────────────────────────────
  if (opts.alertReminders) {
    for (const r of reminders) {
      if (r.done) continue
      if (r.date === today) {
        out.push({
          type: 'reminder', icon: '🔔', iconBg: 'var(--accent-light)',
          title: 'Reminder today', desc: r.text,
          action: () => navigate('/'), priority: 2,
        })
      } else if (r.date < today) {
        const age = daysBetween(now, new Date(`${r.date}T12:00:00`))
        out.push({
          type: 'reminder_past', icon: '⏰', iconBg: 'var(--amber-bg)',
          title: `Overdue reminder (${age}d ago)`, desc: r.text,
          action: () => navigate('/'), priority: 3,
        })
      }
    }
  }

  // ── Stale projects ──────────────────────────────────────────────────────
  if (opts.alertStaleProject) {
    const staleDays = (opts.alertStaleDays as number | undefined) ?? 14
    for (const p of projects) {
      if (p.status !== 'In progress') continue
      const dates: Date[] = [
        ...expenses.filter((e) => e.projId === p.id).map((e) => new Date(e.date)),
        ...schedules.filter((s) => s.projId === p.id).map((s) => new Date(s.date)),
      ]
      if (!dates.length) continue
      const latest = new Date(Math.max(...dates.map((d) => d.getTime())))
      const age = daysBetween(now, latest)
      if (age >= staleDays) {
        out.push({
          type: 'stale', icon: '💤', iconBg: 'rgba(255,255,255,.04)',
          title: `No activity — ${p.name}`,
          desc: `${age} days since last expense or scheduled work`,
          action: () => navigate('/projects'), priority: 4,
        })
      }
    }
  }

  // ── Estimate expiry / client portal / approval state ───────────────────
  const todayIso = today
  const soonCap = (() => {
    const d = new Date(); d.setDate(d.getDate() + 3)
    return d.toISOString().split('T')[0]!
  })()
  for (const est of estimates) {
    if (!est) continue
    const client = est.clientId != null ? state.clients.find((c) => c.id === est.clientId) : null
    const label = client ? `${client.name} — ${est.name || 'Estimate'}` : (est.name || 'Estimate')

    // V1 uses est.expiresAt; V2 domain has expireDate. Read either.
    const expireIso = (est as unknown as { expiresAt?: string }).expiresAt ?? est.expireDate
    if (expireIso && !est.projId && est.clientPortalStatus !== 'accepted') {
      if (expireIso < todayIso) {
        const exp = new Date(`${expireIso}T12:00:00`)
        if (daysBetween(now, exp) <= 3) {
          out.push({
            type: 'est_expired', icon: '⏰', iconBg: 'var(--red-bg)',
            title: `${label} estimate expired`,
            desc: `Expired on ${fmtDate(expireIso)}`,
            action: () => navigate('/estimates'), priority: 2,
            dismissable: true, key: `est_expired_${est.id}`,
          })
        }
      } else if (expireIso <= soonCap) {
        const diff = daysBetween(new Date(`${expireIso}T12:00:00`), new Date(`${todayIso}T12:00:00`))
        const when = diff === 0 ? 'today' : diff === 1 ? 'tomorrow' : `in ${diff} days`
        out.push({
          type: 'est_expiring', icon: '⏰', iconBg: 'var(--amber-bg)',
          title: `${label} estimate expires ${when}`,
          desc: `Expires on ${fmtDate(expireIso)}`,
          action: () => navigate('/estimates'), priority: 3,
          dismissable: true, key: `est_expiring_${est.id}`,
        })
      }
    }

    // Client portal response — if usePortalResponses just flipped the state
    // it will also have pushed the token into opts.portalUnreadTokens. Carry
    // those tokens on the alert so actioning / dismissing the row clears them
    // (V1 parity: public/app/index.html:13245, :20956).
    const estPortalTokens = (est.portalTokens ?? []).filter(
      (t) => (opts.portalUnreadTokens ?? []).includes(t),
    )
    if (est.clientPortalStatus === 'accepted' && !est.projId) {
      out.push({
        type: 'portal_accepted', icon: '✅', iconBg: 'var(--green-bg)',
        title: `Client accepted — ${est.name || 'Untitled'}`,
        desc: 'Ready to create project',
        action: () => navigate('/estimates'), priority: 1,
        dismissable: true, key: `portal_accepted_${est.id}`,
        portalTokens: estPortalTokens.length ? estPortalTokens : undefined,
      })
    } else if (est.clientPortalStatus === 'declined') {
      out.push({
        type: 'portal_declined', icon: '✖', iconBg: 'var(--red-bg)',
        title: `Client declined — ${est.name || 'Untitled'}`,
        desc: 'Estimate was declined via portal',
        action: () => navigate('/estimates'), priority: 2,
        dismissable: true, key: `portal_declined_${est.id}`,
        portalTokens: estPortalTokens.length ? estPortalTokens : undefined,
      })
    }

    // Internal approval status
    if (est.approvalStatus === 'rejected' && est.clientPortalStatus !== 'declined') {
      out.push({
        type: 'estimate_rejected', icon: '✗', iconBg: 'var(--red-bg)',
        title: `Estimate declined — ${est.name || 'Untitled'}`,
        desc: est.rejectedReason || 'Marked as rejected',
        action: () => navigate('/estimates'), priority: 2,
        dismissable: true, key: `est_rejected_${est.id}_${est.rejectedAt || ''}`,
      })
    } else if (
      est.approvalStatus === 'approved'
      && est.approvedBy
      && !est.projId
      && est.clientPortalStatus !== 'accepted'
    ) {
      out.push({
        type: 'estimate_approved', icon: '✓', iconBg: 'var(--green-bg)',
        title: `Estimate approved — ${est.name || 'Untitled'}`,
        desc: `Approved by ${est.approvedBy} — ready to create project`,
        action: () => navigate('/estimates'), priority: 2,
        dismissable: true, key: `est_approved_${est.id}_${est.approvedAt || ''}`,
      })
    }
  }

  // ── Recurring expense due soon ─────────────────────────────────────────
  const soon = new Date(now); soon.setDate(soon.getDate() + 3)
  const soonIso = soon.toISOString().split('T')[0]!
  for (const e of expenses) {
    if (!e.recurring || !e.nextRecur) continue
    if (e.nextRecur > soonIso) continue
    const diff = daysBetween(new Date(`${e.nextRecur}T12:00:00`), now)
    const when = diff <= 0 ? 'Due today' : diff === 1 ? 'Due tomorrow' : `Due in ${diff} days`
    const label = typeof e.recurring === 'string' ? e.recurring : 'recurring'
    out.push({
      type: 'recurring', icon: '🔁', iconBg: 'var(--accent-light)',
      title: `Recurring expense — ${fmt(e.amount)}`,
      desc: `${e.whoName ?? ''} · ${label} · ${when}`,
      action: () => navigate('/expenses'), priority: 3,
    })
  }

  // ── Business doc expiry (insurance / bond / workers' comp) ─────────────
  const docs: Array<{ key: keyof typeof opts; label: string; icon: string }> = [
    { key: 'bizInsuranceExpDate' as const, label: 'Certificate of Insurance', icon: '🛡️' },
    { key: 'bizBondExpDate' as const,      label: 'Surety Bond',              icon: '📜' },
    { key: 'bizWcExpDate' as const,        label: 'Workers Comp Certificate', icon: '🏥' },
  ]
  for (const d of docs) {
    const iso = opts[d.key] as string | undefined
    if (!iso) continue
    const expDate = new Date(`${iso}T00:00:00`)
    const diff = daysBetween(expDate, now)
    if (diff < 0) {
      out.push({
        type: 'doc_expired', icon: d.icon, iconBg: 'var(--red-bg)',
        title: `${d.label} expired`,
        desc: `Expired on ${fmtDate(iso)}`,
        action: () => navigate('/options'), priority: 1,
      })
    } else if (diff <= 30) {
      out.push({
        type: 'doc_expiring', icon: d.icon, iconBg: 'var(--amber-bg)',
        title: `${d.label} expiring soon`,
        desc: `Expires on ${fmtDate(iso)} (${diff} days)`,
        action: () => navigate('/options'), priority: 2,
      })
    }
  }

  out.sort((a, b) => a.priority - b.priority)
  return out.filter((a) => !(a.key && dismissed[a.key]))
}

export default function AlertBell() {
  const { data: state } = useAppState()
  const optsMut = useOptsMutation()
  const navigate = useNavigate()
  const [open, setOpen] = useState(false)
  const [dismissed, setDismissedState] = useState<Record<string, number>>(() => getDismissed())
  const wrapRef = useRef<HTMLDivElement>(null)

  // Incoming shared estimates (estshare__ docs). These are pending only —
  // listMySharedEstimates already filters to status=pending. Count adds to
  // the bell badge so the client knows an estimate arrived without opening
  // the Estimates tab. V1 parity: updateAlertBell() included shared estimate
  // count in the bell (public/app/index.html L7079).
  const { data: incomingEstimates } = useIncomingSharedEstimates()
  const incomingEstCount = incomingEstimates?.length ?? 0

  const alerts = useMemo<AlertItem[]>(() => {
    if (!state) return []
    return generateAlerts({ state, navigate, dismissed })
  }, [state, navigate, dismissed])

  // Strip a batch of portal tokens from opts.portalUnreadTokens. Fires when a
  // portal-response alert is clicked or dismissed. No-op if no tokens left.
  const ackPortalTokens = useCallback((tokens: string[] | undefined) => {
    if (!tokens || !tokens.length || !state) return
    const current = state.opts.portalUnreadTokens ?? []
    const next = current.filter((t) => !tokens.includes(t))
    if (next.length === current.length) return
    optsMut.mutate({ portalUnreadTokens: next })
  }, [state, optsMut])

  const totalCount = alerts.length + incomingEstCount
  const hasAlerts = totalCount > 0
  const count = totalCount
  const badgeText = count > 9 ? '9+' : String(count)

  // Outside-click close
  useEffect(() => {
    if (!open) return
    function onDown(e: MouseEvent | PointerEvent) {
      if (!wrapRef.current) return
      if (wrapRef.current.contains(e.target as Node)) return
      setOpen(false)
    }
    window.addEventListener('pointerdown', onDown)
    return () => window.removeEventListener('pointerdown', onDown)
  }, [open])

  // Escape to close
  useEffect(() => {
    if (!open) return
    function onKey(e: KeyboardEvent) { if (e.key === 'Escape') setOpen(false) }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [open])

  const dismissOne = useCallback((key: string) => {
    const next = { ...getDismissed(), [key]: Date.now() }
    setDismissed(next)
    setDismissedState(next)
    const a = alerts.find((x) => x.key === key)
    if (a) ackPortalTokens(a.portalTokens)
  }, [alerts, ackPortalTokens])

  const clearAll = useCallback(() => {
    const next = { ...getDismissed() }
    const drop: string[] = []
    for (const a of alerts) {
      if (a.dismissable && a.key) next[a.key] = Date.now()
      if (a.portalTokens) drop.push(...a.portalTokens)
    }
    setDismissed(next)
    setDismissedState(next)
    if (drop.length) ackPortalTokens(drop)
  }, [alerts, ackPortalTokens])

  const anyDismissable = alerts.some((a) => a.dismissable)

  function runAction(a: AlertItem) {
    setOpen(false)
    // Navigating to the estimate counts as an ack — clear any portal tokens
    // attached to this row so the red dot on Projects stops pulsing.
    ackPortalTokens(a.portalTokens)
    try { a.action() } catch (err) { console.warn('alert action failed', err) }
  }

  return (
    <div ref={wrapRef} style={{ position: 'relative' }}>
      <button
        className={`alert-bell${hasAlerts ? ' has-alerts' : ''}`}
        aria-label={hasAlerts ? `${count} alerts` : 'No alerts'}
        aria-haspopup="true"
        aria-expanded={open}
        title="Alerts"
        onClick={() => setOpen((o) => !o)}
        type="button"
      >
        🔔
        {hasAlerts && (
          <span className="alert-badge" aria-hidden>
            {badgeText}
          </span>
        )}
      </button>

      {open && (
        <div
          role="dialog"
          aria-label="Alerts"
          style={{
            position: 'absolute',
            top: 'calc(100% + 6px)',
            right: 0,
            width: 360,
            maxHeight: 480,
            overflowY: 'auto',
            background: 'var(--surface)',
            border: '1px solid var(--border)',
            borderRadius: 'var(--radius)',
            boxShadow: '0 12px 32px rgba(0,0,0,0.35)',
            zIndex: 100,
            padding: 0,
          }}
        >
          <div style={{
            display: 'flex', alignItems: 'center', justifyContent: 'space-between',
            padding: '10px 14px', borderBottom: '1px solid var(--border)',
            position: 'sticky', top: 0, background: 'var(--surface)', zIndex: 1,
          }}>
            <div style={{ fontWeight: 600, fontSize: 13 }}>
              {`Alerts${totalCount ? ` (${totalCount})` : ''}`}
            </div>
            <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
              {anyDismissable && (
                <button
                  className="btn-ghost btn-sm"
                  onClick={(e) => { e.stopPropagation(); clearAll() }}
                  style={{ fontSize: 11, padding: '4px 8px' }}
                  type="button"
                >
                  Clear all
                </button>
              )}
              <button
                onClick={() => setOpen(false)}
                aria-label="Close alerts"
                style={{
                  background: 'transparent', border: 'none', color: 'var(--text-faint)',
                  cursor: 'pointer', fontSize: 14, padding: '4px 8px', borderRadius: 4,
                }}
                type="button"
              >
                ✕
              </button>
            </div>
          </div>

          {/* Incoming shared estimates — one row per pending estshare__ doc.
              Navigate to /estimates (Estimates tab) on click. Not dismissable
              (they clear automatically once accepted / declined / expired). */}
          {incomingEstimates && incomingEstimates.map((est) => (
            <div
              key={est.id}
              onClick={() => { setOpen(false); navigate('/estimates') }}
              role="button"
              tabIndex={0}
              onKeyDown={(ev) => { if (ev.key === 'Enter' || ev.key === ' ') { ev.preventDefault(); setOpen(false); navigate('/estimates') } }}
              style={{
                display: 'flex', alignItems: 'center', gap: 8,
                padding: '10px 14px', borderBottom: '1px solid var(--border)',
                cursor: 'pointer',
              }}
            >
              <div
                style={{
                  background: 'var(--accent-light)', borderRadius: 8, width: 32, height: 32,
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  fontSize: 16, flexShrink: 0,
                }}
                aria-hidden
              >
                📥
              </div>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontWeight: 600, fontSize: 13, color: 'var(--text)' }}>
                  Incoming estimate
                </div>
                <div style={{ fontSize: 12, color: 'var(--text-muted)', marginTop: 2 }}>
                  From {est.fromName || est.fromTeamCode || 'a contractor'} — tap to view
                </div>
              </div>
            </div>
          ))}

          {totalCount === 0 ? (
            <div style={{ padding: '30px 14px', textAlign: 'center' }}>
              <div style={{ fontSize: 24, marginBottom: 8, opacity: 0.4 }}>✓</div>
              <div style={{ fontSize: 13, color: 'var(--text-faint)' }}>
                All clear — no alerts
              </div>
            </div>
          ) : alerts.length === 0 ? null : (
            alerts.map((a, i) => (
              <div
                key={`${a.type}-${a.key ?? i}`}
                onClick={() => runAction(a)}
                role="button"
                tabIndex={0}
                onKeyDown={(ev) => { if (ev.key === 'Enter' || ev.key === ' ') { ev.preventDefault(); runAction(a) } }}
                style={{
                  display: 'flex', alignItems: 'center', gap: 8,
                  padding: '10px 14px', borderBottom: '1px solid var(--border)',
                  cursor: 'pointer',
                }}
              >
                <div
                  style={{
                    background: a.iconBg, borderRadius: 8, width: 32, height: 32,
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    fontSize: 16, flexShrink: 0,
                  }}
                  aria-hidden
                >
                  {a.icon}
                </div>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontWeight: 600, fontSize: 13, color: 'var(--text)' }}>{a.title}</div>
                  <div style={{ fontSize: 12, color: 'var(--text-muted)', marginTop: 2 }}>{a.desc}</div>
                </div>
                {a.dismissable && a.key && (
                  <button
                    onClick={(e) => { e.stopPropagation(); dismissOne(a.key!) }}
                    aria-label="Dismiss alert"
                    title="Dismiss"
                    style={{
                      background: 'transparent', border: 'none', color: 'var(--text-faint)',
                      cursor: 'pointer', fontSize: 14, padding: '4px 8px', borderRadius: 4,
                      flexShrink: 0,
                    }}
                    type="button"
                  >
                    ✕
                  </button>
                )}
              </div>
            ))
          )}
        </div>
      )}
    </div>
  )
}
